import type { ChangeEvent } from 'react';
import Input from 'creative-ui/uikit/Input';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import SvgSearch from 'creative-ui/uikit/svg/search';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryFlag } from '@/components/CountryFlag/CountryFlag';
import { Flex } from '@/components/Layout/Flex/Flex';
import { ListBox } from '@/components/ListBox/ListBox';
import { trackClick } from '@/lib/tagging';

import { useMfaCountryCodesManager } from '../hooks/useMfaCountryCodesManager';

function CountryCodeList() {
  const { t } = useTranslation('mfaCountryCodes');
  const { handleSelectCountryCode, phonePrefixes } =
    useMfaCountryCodesManager();

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // i18n strings
  const searchLabel = t('list.searchLabel');
  const searchPlaceholder = t('list.searchPlaceholder');
  const noCountriesFound = t('list.noCountriesFound');
  const genericError = t('errors.generic');

  const filteredPrefixes = useMemo(() => {
    if (!search.trim()) return phonePrefixes;
    const searchLower = search.toLowerCase();
    return phonePrefixes.filter(
      (prefix) =>
        prefix.country.toLowerCase().includes(searchLower) ||
        prefix.phone_prefix.includes(searchLower) ||
        prefix.country_code.toLowerCase().includes(searchLower),
    );
  }, [phonePrefixes, search]);

  const onSelectCountry = useCallback(
    async (value: string) => {
      const [countryCode, phonePrefix] = value.split('|');

      if (!countryCode || !phonePrefix) {
        return;
      }

      trackClick('mfa/sms/country/select');
      setError(null);
      try {
        await handleSelectCountryCode(countryCode, phonePrefix);
      } catch {
        setError(genericError);
      }
    },
    [handleSelectCountryCode, genericError],
  );

  // Build one selectable option per country, flag first
  const options = useMemo(
    () =>
      filteredPrefixes.map((prefix) => ({
        value: `${prefix.country_code}|${prefix.phone_prefix}`,
        label: `${prefix.country} (${prefix.phone_prefix})`,
        icon: <CountryFlag countryCode={prefix.country_code} size="sm" />,
      })),
    [filteredPrefixes],
  );

  return (
    <Flex direction="column" gapY={16}>
      <Input
        name="search"
        type="search"
        hiddenLabel
        placeholder={searchPlaceholder}
        value={search}
        inputPicto={<SvgSearch />}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearch(event.target.value);
        }}>
        {searchLabel}
      </Input>

      {error && <StatusMessage mode="error">{error}</StatusMessage>}

      {filteredPrefixes.length > 0 ? (
        <div style={{ maxHeight: 350, overflowY: 'auto' }}>
          <ListBox
            id="country-codes-list"
            options={options}
            onSelect={onSelectCountry}
            showArrow
            showSeparator
          />
        </div>
      ) : (
        <p className="u-textCenter">{noCountriesFound}</p>
      )}
    </Flex>
  );
}

export default CountryCodeList;
